import { observer } from 'mobx-react-lite';
import React from 'react';
import { RefundInformationDialog } from '#/components/BookingSummaryPanel/components/RefundInformationDialog';
import { useGetBooking } from '#/hooks/useGetBooking';
import { useTranslation } from '#/hooks/useTranslation';
import { RentalsReservationInstance } from '@jucy-nakuru/common/services/JucyRentalsApi/RentalsReservation';
import { ValidateUpdateResponse } from '@jucy/rentals-api-client/rentals-api-v3/models';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import Currency from '../../Currency';
import BookingSummaryPanelSection from './BookingSummaryPanelSection';

export type BookingSummaryBalanceDueProps = {
    reservation: RentalsReservationInstance;
    amendment?: ValidateUpdateResponse;
};

export const BookingSummaryBalanceDue: React.FC<BookingSummaryBalanceDueProps> = observer(function BookingSummaryBalanceDueComponent({
    reservation,
    amendment,
}) {
    const isRefundDue = reservation?.computedBalanceDue.value < 0;
    const { t } = useTranslation();
    const { data: booking } = useGetBooking(reservation?.reservationReference);

    const originalBalance = {
        currencyCode: reservation?.computedBalanceDue.currencyCode,
        value: Math.abs(reservation?.computedBalanceDue.value),
    };
    const newTotal = amendment?.updates?.totalPrice.value || 0;
    const paymentsTotal = booking?.payments?.reduce((acc, payment) => acc + (payment.total?.value || 0), 0) || 0;
    const newBalance = {
        ...amendment?.updates?.totalPrice,
        value: newTotal - paymentsTotal,
    };
    const hasNewBalanceDue = Boolean(newTotal > 0 && newBalance.value !== originalBalance.value);
    const amendedExcess = amendment?.updates?.lines?.find((l) => l.category === 'InsuranceFee' && !l.isExcluded);

    if (reservation?.computedBalanceDue?.value !== 0) {
        return (
            <Box sx={{ minHeight: '100px' }}>
                <Divider sx={{ mb: 1, mt: 3 }} />
                <BookingSummaryPanelSection
                    display="flex"
                    justifyContent="space-between"
                    sx={{ textDecoration: hasNewBalanceDue ? 'line-through' : undefined }}
                >
                    <Typography variant="button" component="h3">
                        {isRefundDue ? t('summary.refund.label', 'Refund due') : t('summary.balance-due.information', 'Balance due')}
                        {isRefundDue ? <RefundInformationDialog /> : null}
                    </Typography>
                    <Typography variant="button" component="h3" align="right">
                        <Currency>{originalBalance}</Currency>
                    </Typography>
                </BookingSummaryPanelSection>
                {hasNewBalanceDue && (
                    <BookingSummaryPanelSection display="flex" justifyContent="space-between">
                        <Typography variant="button" component="h3">
                            New Balance due
                        </Typography>
                        <Typography variant="button" component="h3" align="right">
                            <Currency>{newBalance}</Currency>
                        </Typography>
                    </BookingSummaryPanelSection>
                )}
            </Box>
        );
    }

    if (newBalance.value === amendedExcess?.total.value) {
        return (
            <Box sx={{ minHeight: '100px' }}>
                <Divider sx={{ mb: 1, mt: 3 }} />
                <BookingSummaryPanelSection display="flex" justifyContent="space-between">
                    <Typography variant="button" component="h3">
                        Balance due
                    </Typography>
                    <Typography variant="button" component="h3" align="right">
                        <Currency>{newBalance}</Currency>
                    </Typography>
                </BookingSummaryPanelSection>
            </Box>
        );
    }

    return null;
});
